export function buildPostsById(posts = []) {
  const postsById = {};

  posts.forEach((post) => {
    if (!post || typeof post !== "object" || !post.id) {
      return;
    }

    postsById[post.id] = post;
  });

  return postsById;
}

function resolvePostOwnerKey(post) {
  if (!post || typeof post !== "object") {
    return null;
  }

  if (typeof post.userId === "string" && post.userId.trim()) {
    return `id:${post.userId.trim()}`;
  }

  if (typeof post.user === "string" && post.user.trim()) {
    return `user:${post.user.trim().toLowerCase()}`;
  }

  return null;
}

export function resolveRootPost(post, postsById) {
  if (!post || typeof post !== "object") {
    return null;
  }

  const parentId =
    typeof post.sharedPost?.id === "string" ? post.sharedPost.id : null;

  if (post.postType !== "share" || !parentId) {
    return post;
  }

  const postId = typeof post.id === "string" ? post.id : null;
  const visited = new Set(postId ? [postId] : []);
  let currentParentId = parentId;
  let currentFallbackPost = post.sharedPost;

  while (currentParentId) {
    if (visited.has(currentParentId)) {
      return currentFallbackPost || { id: currentParentId };
    }

    visited.add(currentParentId);

    const parentPost = postsById[currentParentId];
    if (!parentPost || typeof parentPost !== "object") {
      return currentFallbackPost || { id: currentParentId };
    }

    currentFallbackPost = parentPost;

    const nextParentId =
      parentPost.postType === "share" &&
      typeof parentPost.sharedPost?.id === "string"
        ? parentPost.sharedPost.id
        : null;

    if (!nextParentId) {
      return parentPost;
    }

    currentParentId = nextParentId;
  }

  return post;
}

function resolveRootPostId(post, postsById) {
  const rootPost = resolveRootPost(post, postsById);
  return typeof rootPost?.id === "string" ? rootPost.id : null;
}

export function buildShareCountLookup(posts = []) {
  const postsById = buildPostsById(posts);
  const countsByRootId = {};
  const seenShareIds = new Set();

  posts.forEach((post) => {
    if (!post || post.postType !== "share") {
      return;
    }

    if (post.id && seenShareIds.has(post.id)) {
      return;
    }

    if (post.id) {
      seenShareIds.add(post.id);
    }

    const rootPostId = resolveRootPostId(post, postsById);
    if (!rootPostId) {
      return;
    }

    const rootPost = resolveRootPost(post, postsById);
    const shareOwnerKey = resolvePostOwnerKey(post);
    const rootOwnerKey = resolvePostOwnerKey(rootPost);

    if (shareOwnerKey && rootOwnerKey && shareOwnerKey === rootOwnerKey) {
      return;
    }

    countsByRootId[rootPostId] = (countsByRootId[rootPostId] || 0) + 1;
  });

  return {
    postsById,
    countsByRootId,
  };
}

export function getShareCountForPost(post, lookup) {
  const rootPostId = resolveRootPostId(post, lookup?.postsById || {});
  if (!rootPostId) {
    return 0;
  }

  return lookup?.countsByRootId?.[rootPostId] || 0;
}
